/**
 * Quote-flow pieces shared by the customer portal (/quote) and the admin console's quoting center. Presentational
 * only: the apps own state, validation and API calls and pass display strings in. Markup and class names are the
 * portal's own (moved from apps/web/src/pages/portal), so the portal renders exactly as before.
 * Styles: @flexfreight/ui/quote.css (+ carrier.css for the logo chip).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { CarrierLogo, chipShowsName } from "./carrier.js";
import { FilterToolbar, InfoTip, Select, TextField, Toggle, type SelectOption } from "./controls.js";
import { AddressAutocomplete, useZipAutofill, ZipCityChoices, ZIP_UNKNOWN, type AddressSource, type PickedAddress, type PostalLookup } from "./address.js";
import { carrierDoneText, carrierProgressText, type StreamCarrier } from "./stream.js";

const countFmt = new Intl.NumberFormat("en-US");
const fmtCount = (n: number): string => countFmt.format(n);

/* ---------------------------------------------------------------- icons (stroke, currentColor, decorative) */
function Svg({ children, vb = "0 0 24 24" }: { children: ReactNode; vb?: string }) {
  return (
    <svg viewBox={vb} width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}
export function QuoteCheckIcon() { return <Svg vb="0 0 16 16"><path d="M3.5 8.5l3 3 6-6.5" strokeWidth="2" /></Svg>; }
export function QuotePinIcon() { return <Svg><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.4" /></Svg>; }
export function QuoteCalendarIcon() { return <Svg><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></Svg>; }
export function QuotePlusIcon() { return <Svg><path d="M12 5v14M5 12h14" /></Svg>; }
export function QuoteArrowIcon() { return <Svg><path d="M5 12h14M13 6l6 6-6 6" /></Svg>; }

const ACC_PATHS: Record<string, ReactNode> = {
  liftgate: <><path d="M3 18h12M5 18V8h8v10M15 18l5-3" /><path d="M9 5v6M7 7l2-2 2 2" /></>,
  inside: <><path d="M4 20V6l8-3 8 3v14" /><path d="M10 20v-6h4v6" /><path d="M8 12h-4" /></>,
  appointment: <><circle cx="12" cy="12" r="8" /><path d="M12 7.5V12l3 2" /></>,
  notify: <><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 0 0 4 0" /></>,
  residential: <><path d="M3.5 11.5L12 4l8.5 7.5" /><path d="M6 10v10h12V10" /><path d="M10 20v-5h4v5" /></>,
  limited_access: <><circle cx="12" cy="12" r="8" /><path d="M6.5 6.5l11 11" /></>,
  construction: <><path d="M4 20h16M6 20l5-15h2l5 15" /><path d="M8 14h8M9.5 9.5h5" /></>,
  trade_show: <><rect x="3.5" y="4" width="17" height="11" rx="1.5" /><path d="M8 20l4-5 4 5M7.5 9.5h9" /></>,
  protect_from_freeze: <><path d="M12 3v18M4.5 7.5l15 9M19.5 7.5l-15 9" /><path d="M9.5 4.5L12 7l2.5-2.5M9.5 19.5L12 17l2.5 2.5" /></>,
  sort_segregate: <><rect x="3.5" y="4" width="7" height="7" rx="1" /><rect x="13.5" y="4" width="7" height="7" rx="1" /><rect x="8.5" y="14" width="7" height="7" rx="1" /></>,
};
export function AccessorialIcon({ code }: { code: string }) { return <Svg>{ACC_PATHS[code] ?? <circle cx="12" cy="12" r="7" />}</Svg>; }

/* ---------------------------------------------------------------- progress track + step head */
export type QuoteStepState = "done" | "current" | "todo";

/** The wizard's progress track: one segment per step, numbered node, lime up to the current step. */
export function QuoteSteps({ steps, count, label = "Quote steps" }: {
  steps: ReadonlyArray<{ label: string; state: QuoteStepState }>;
  /** "Step 2 of 5 · About a minute" (decorative; the list carries the state). */
  count: ReactNode;
  label?: string;
}) {
  return (
    <nav className="ff-qsteps ff-qsteps--wizard" aria-label={label} style={{ ["--ff-qsteps-n" as string]: steps.length }}>
      <p className="ff-qsteps__count" aria-hidden="true">{count}</p>
      <ol className="ff-qsteps__list">
        {steps.map(({ label: name, state }, i) => (
          <li key={name} className={`ff-qsteps__step ff-qsteps__step--${state}`} aria-current={state === "current" ? "step" : undefined} data-done={state === "done" || undefined}>
            <span className="ff-qsteps__dot" aria-hidden="true">
              {state === "done" ? <QuoteCheckIcon /> : i + 1}
            </span>
            <span className="ff-qsteps__label">{name}</span>
            {state === "done" ? <span className="ff-visually-hidden"> (completed)</span> : null}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** "01  Where it ships from (i)": the numbered tile and title that open each quote step. */
export function QuoteStepHead({ n, title, hint, id, headingRef, focusTarget = false }: {
  n: number;
  title: ReactNode;
  hint?: ReactNode;
  id?: string;
  headingRef?: Ref<HTMLHeadingElement>;
  /** The wizard moves focus to the title on each step (tabIndex -1). */
  focusTarget?: boolean;
}) {
  return (
    <header className="ff-qw__head">
      <span className="ff-qw__stepno" aria-hidden="true">{String(n).padStart(2, "0")}</span>
      <div className="ff-qw__titles">
        <div className="ff-qw__titlerow">
          <h2 id={id} className="ff-qw__title" tabIndex={focusTarget ? -1 : undefined} ref={headingRef}>
            {title}
          </h2>
          {hint ? <InfoTip>{hint}</InfoTip> : null}
        </div>
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- address card + fields */
/** Origin or destination as one card: pin, "Pickup address", the place so far, then whatever the app puts below. */
export function AddressCard({ kind, place, empty = "Search your address book or type it in", children }: {
  kind: "origin" | "destination";
  place: string;
  empty?: string;
  children: ReactNode;
}) {
  return (
    <div className={`ff-qaddr ff-qaddr--${kind}`}>
      <div className="ff-qaddr__top">
        <span className="ff-qaddr__pin" aria-hidden="true"><QuotePinIcon /></span>
        <div className="ff-qaddr__heading">
          <p className="ff-qaddr__kind">{kind === "origin" ? "Pickup address" : "Delivery address"}</p>
          <p className="ff-qaddr__place">{place || empty}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export type AddressValue<C extends string = string, T extends string = string> = {
  companyName: string;
  line1: string;
  city: string;
  region: string;
  postalCode: string;
  country: C;
  type: T;
};

/** The region and postal labels follow the country, so the field says what to type. */
export const regionLabel = (c: string) => (c === "CA" ? "Province" : "State");
export const postalLabel = (c: string) => (c === "US" ? "ZIP code" : "Postal code");

/**
 * Address fields on one deliberate grid (container query in quote.css): Company 2fr | Street spans the rest;
 * City 2fr | State .8fr | ZIP 1fr | Country 1.4fr; Location type under Company. Every control is --ff-field-h.
 * Without `countries` the country column is dropped (US-only callers) and the tracks close up.
 *
 * ZIP-first (`zipFirst`, address.css): ZIP | City | State | Country come first and are all a quote needs; company
 * and street follow, optional until booking. With `postalLookup` a valid ZIP fills city and state (still editable)
 * and offers the other city names it covers; with `streetSource` the street suggests full addresses.
 */
export function AddressFields<C extends string, T extends string>({ prefix, value, onChange, errors, countries, locationTypes, locationHint = "Residential, limited-access and no-dock stops add fees.", streetOptional, streetHint, companyHint, zipFirst = false, postalLookup, streetSource }: {
  prefix: string;
  value: AddressValue<C, T>;
  onChange: (v: AddressValue<C, T>) => void;
  errors: Record<string, string | undefined>;
  countries?: SelectOption<C>[];
  locationTypes: SelectOption<T>[];
  locationHint?: ReactNode;
  /** Quotes rate on ZIP, city and state; the street is needed before booking. Defaults to `zipFirst`. */
  streetOptional?: boolean;
  streetHint?: ReactNode;
  companyHint?: ReactNode;
  zipFirst?: boolean;
  postalLookup?: PostalLookup;
  streetSource?: AddressSource;
}) {
  const set = <K extends keyof AddressValue<C, T>>(k: K, v: AddressValue<C, T>[K]) => onChange({ ...value, [k]: v });
  const zip = useZipAutofill(value, onChange, postalLookup);
  const optionalStreet = streetOptional ?? zipFirst;
  const pickStreet = (a: PickedAddress) => {
    const country = a.country && (!countries || countries.some((c) => c.value === a.country)) ? (a.country as C) : value.country;
    onChange({ ...value, line1: a.line1 || value.line1, city: a.city || value.city, region: a.state || value.region, postalCode: a.postalCode || value.postalCode, country });
  };
  const company = <TextField key="company" className="ff-qaddr__company" label="Company" optional hint={companyHint} autoComplete="organization" value={value.companyName} onChange={(e) => set("companyName", e.target.value)} />;
  const street = (
    <AddressAutocomplete key="street" className="ff-qaddr__street" label="Street address" optional={optionalStreet} hint={optionalStreet ? streetHint : undefined} error={errors[`${prefix}.line1`]} value={value.line1} onChange={(t) => set("line1", t)} onPick={pickStreet} source={streetSource} />
  );
  const cityHint = zip.status === "found" ? <ZipCityChoices cities={zip.cities} current={value.city} onPick={zip.pickCity} /> : null;
  const city = <TextField key="city" className="ff-qaddr__city" label="City" aria-required autoComplete="address-level2" hint={cityHint} error={errors[`${prefix}.city`]} value={value.city} onChange={(e) => set("city", e.target.value)} />;
  const region = <TextField key="region" className="ff-qaddr__region ff-qf--caps" label={regionLabel(value.country)} aria-required maxLength={2} autoComplete="address-level1" example={value.country === "CA" ? "ON" : "NJ"} error={errors[`${prefix}.region`]} value={value.region} onChange={(e) => set("region", e.target.value.toUpperCase())} />;
  const postal = (
    <TextField key="postal" className="ff-qaddr__postal" label={postalLabel(value.country)} aria-required {...(countries ? {} : { inputMode: "numeric" as const })} autoComplete="postal-code" hint={zip.status === "unknown" ? ZIP_UNKNOWN : undefined} error={errors[`${prefix}.postalCode`]} value={value.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
  );
  const country = countries ? <Select key="country" className="ff-qaddr__country" label="Country" options={countries} value={value.country} autoComplete="country" onChange={(v) => set("country", v)} /> : null;
  const type = <Select key="type" className="ff-qaddr__type" label="Location type" options={locationTypes} value={value.type} onChange={(v) => set("type", v)} hint={locationHint} />;
  const grid = ["ff-qaddr__grid", countries ? null : "ff-qaddr__grid--domestic", zipFirst ? "ff-qaddr__grid--zip" : null].filter(Boolean).join(" ");
  return <div className={grid}>{zipFirst ? [postal, city, region, country, company, street, type] : [company, street, city, region, postal, country, type]}</div>;
}

/** Pickup date row: calendar tile, the app's date field, and a note beside it. */
export function PickupDateRow({ field, note }: { field: ReactNode; note: ReactNode }) {
  return (
    <div className="ff-qw__date">
      <span className="ff-qw__dateicon" aria-hidden="true"><QuoteCalendarIcon /></span>
      {field}
      <p className="ff-qw__datenote" aria-live="polite">{note}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- freight lines */
export type FreightLineValue<P extends string = string, K extends string = string> = {
  packaging: P;
  quantity: string;
  weightLb: string;
  lengthIn: string;
  widthIn: string;
  heightIn: string;
  description: string;
  freightClass: K;
};

const packLabel = (k: string) => k[0]!.toUpperCase() + k.slice(1);

/**
 * One freight line on one three-column grid (container query in quote.css): Packaging | Quantity | Total weight,
 * then Length × Width × Height on the same tracks, then Commodity (2 tracks) | Freight class. The app adds the
 * density strip, toggles and anything else below as children.
 */
export function FreightLine<P extends string, K extends string>({ index, value, onChange, error, packagingOptions, classOptions, onRemove, children }: {
  index: number;
  value: FreightLineValue<P, K>;
  onChange: (patch: Partial<FreightLineValue<P, K>>) => void;
  error: (field: keyof FreightLineValue) => string | undefined;
  packagingOptions: SelectOption<P>[];
  classOptions: SelectOption<K>[];
  onRemove?: () => void;
  children?: ReactNode;
}) {
  const u = value;
  const i = index;
  const qty = Number(u.quantity) || 0;
  const wt = Number(u.weightLb) || 0;
  return (
    <fieldset className="ff-unit ff-qline">
      <legend className="ff-qline__legend">
        <span className="ff-qline__n">Line {i + 1}</span>
        <span className="ff-qline__sum ff-num">
          {qty > 0 ? `${fmtCount(qty)} ${u.packaging}${qty === 1 ? "" : "s"}` : packLabel(u.packaging)}
          {wt > 0 ? ` · ${fmtCount(wt)} lb` : ""}
          {u.freightClass ? ` · class ${u.freightClass}` : ""}
        </span>
      </legend>
      {onRemove ? (
        <button type="button" className="ff-btn ff-btn--quiet ff-btn--sm ff-qline__remove" onClick={onRemove}>
          <span aria-hidden="true">Remove</span>
          <span className="ff-visually-hidden">Remove line {i + 1}</span>
        </button>
      ) : null}
      <div className="ff-qline__grid">
        <Select label="Packaging" options={packagingOptions} value={u.packaging} onChange={(packaging) => onChange({ packaging } as Partial<FreightLineValue<P, K>>)} />
        <TextField label="Quantity" aria-required inputMode="numeric" error={error("quantity")} value={u.quantity} onChange={(ev) => onChange({ quantity: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
        <TextField label="Total weight" aria-required suffix="lb" inputMode="decimal" example="1,200" error={error("weightLb")} value={u.weightLb} onChange={(ev) => onChange({ weightLb: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
        <div className="ff-qline__dims" role="group" aria-label={`Line ${i + 1} dimensions, inches per unit`}>
          <TextField label="Length" aria-required suffix="in" inputMode="decimal" error={error("lengthIn")} value={u.lengthIn} onChange={(ev) => onChange({ lengthIn: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
          <span className="ff-qline__x" aria-hidden="true">×</span>
          <TextField label="Width" aria-required suffix="in" inputMode="decimal" error={error("widthIn")} value={u.widthIn} onChange={(ev) => onChange({ widthIn: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
          <span className="ff-qline__x" aria-hidden="true">×</span>
          <TextField label="Height" aria-required suffix="in" inputMode="decimal" example="48" error={error("heightIn")} value={u.heightIn} onChange={(ev) => onChange({ heightIn: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
          <p className="ffc-msg ff-qline__dimsnote" aria-hidden="true">Dimensions per handling unit</p>
        </div>
        <TextField className="ff-qline__commodity" label="Commodity" aria-required example="Machine parts on pallets" error={error("description")} value={u.description} onChange={(ev) => onChange({ description: ev.target.value } as Partial<FreightLineValue<P, K>>)} />
        <Select className="ff-qline__class" label="Freight class" options={classOptions} value={u.freightClass} onChange={(freightClass) => onChange({ freightClass } as Partial<FreightLineValue<P, K>>)} />
      </div>
      {children}
    </fieldset>
  );
}

export type LineDensity = { pcf: number; cubicFt: number; suggestedClass: string };

/** Density strip under a freight line: value, meter, the class it rates as, and a reclass warning. */
export function DensityStrip({ line, density: dens, declaredClass, suggest }: { line: number; density: LineDensity | null; declaredClass: string; suggest?: ReactNode }) {
  return (
    <div className="ff-qdens">
      <DensityAnnouncer
        message={
          dens
            ? `Line ${line} rates as class ${dens.suggestedClass}.${declaredClass && declaredClass !== dens.suggestedClass ? ` Declared class ${declaredClass} differs; carriers may reclass.` : ""}`
            : ""
        }
      />
      {dens ? (
        <>
          <p className="ff-qdens__value ff-num"><span className="ff-qdens__label">Density</span> {dens.pcf}<span className="ff-qdens__unit"> lb/ft³</span></p>
          <DensityMeter pcf={dens.pcf} />
          <p className="ff-qdens__class ff-num">{dens.cubicFt} ft³ · rates as density class <strong>{dens.suggestedClass}</strong></p>
        </>
      ) : (
        <p className="ff-qdens__empty"><span className="ff-qdens__label">Density</span> Add weight and all three dimensions to see the class it rates as.</p>
      )}
      {suggest}
      {dens && declaredClass && declaredClass !== dens.suggestedClass ? <p className="ff-density__warn ff-qdens__warn">Declared class {declaredClass} differs from density class {dens.suggestedClass}; carriers may reclass on inspection.</p> : null}
    </div>
  );
}

/**
 * Screen-reader announcement for the density strip. The visible strip updates on every keystroke; this only
 * speaks when the rated class (or the reclass warning) actually changes, and only once typing has paused.
 */
function DensityAnnouncer({ message }: { message: string }) {
  const [spoken, setSpoken] = useState(message);
  useEffect(() => {
    if (message === spoken) return;
    const t = window.setTimeout(() => setSpoken(message), 700);
    return () => window.clearTimeout(t);
  }, [message, spoken]);
  return (
    <span className="ff-visually-hidden" aria-live="polite" aria-atomic="true">
      {spoken}
    </span>
  );
}

/** Density on a 1-30 lb/ft³ scale: low density means a high class. */
function DensityMeter({ pcf }: { pcf: number }) {
  const pct = Math.max(4, Math.min(100, (pcf / 30) * 100));
  return (
    <span className="ff-qdens__meter" aria-hidden="true">
      <span className="ff-qdens__fill" style={{ inlineSize: `${pct}%` }} />
    </span>
  );
}

/* ---------------------------------------------------------------- accessorial tiles */
export type AccessorialTile = { code: string; stop: string; label: string; hint?: string };

/** Extra services as icon tiles in groups ("At pickup", "At delivery", "Whole shipment"); real checkboxes. */
export function AccessorialTiles({ groups, isChecked, onToggle, note }: {
  groups: ReadonlyArray<{ title: string; picks: ReadonlyArray<AccessorialTile> }>;
  isChecked: (t: AccessorialTile) => boolean;
  onToggle: (t: AccessorialTile) => void;
  note?: ReactNode;
}) {
  const uid = useId();
  return (
    <div className="ff-acc ff-qacc">
      {groups.map((g) => {
        const n = g.picks.filter(isChecked).length;
        return (
          <fieldset key={g.title} className="ff-acc__group ff-qacc__group">
            <legend className="ff-qacc__legend">
              {g.title}
              {n > 0 ? <span className="ff-qacc__n ff-num"> · {n} selected</span> : null}
            </legend>
            <div className="ff-qacc__tiles">
              {g.picks.map((a) => (
                <label key={a.code} className="ff-qtile">
                  <input type="checkbox" className="ff-qtile__input" checked={isChecked(a)} onChange={() => onToggle(a)} aria-labelledby={`${uid}-${a.stop}-${a.code}`} aria-describedby={a.hint ? `${uid}-${a.stop}-${a.code}-h` : undefined} />
                  <span className="ff-qtile__icon" aria-hidden="true"><AccessorialIcon code={a.code} /></span>
                  <span className="ff-qtile__text" id={`${uid}-${a.stop}-${a.code}`}>{a.label}</span>
                  {a.hint ? <span className="ff-qtile__hint" id={`${uid}-${a.stop}-${a.code}-h`}>{a.hint}</span> : null}
                  <span className="ff-qtile__check" aria-hidden="true"><QuoteCheckIcon /></span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
      {note ? <p className="ff-qacc__note">{note}</p> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- shipment-so-far card */
/** Live shipment card beside the form: lane, pickup day, facts, a footnote, then anything the app adds. */
export function ShipmentSoFar({ origin, destination, pickup, facts, foot, panelClass = "ff-panel", label = "Shipment so far", children }: {
  origin: string;
  destination: string;
  pickup: { iso: string; text: string } | null;
  facts: ReadonlyArray<{ label: string; value: ReactNode }>;
  foot?: ReactNode;
  panelClass?: string;
  label?: string;
  children?: ReactNode;
}) {
  return (
    <section className={`${panelClass} ff-qlive`} aria-label={label}>
      <p className="ff-qlive__label">Your shipment</p>
      <ol className="ff-qlive__lane">
        <li className={origin ? "is-set" : undefined}>
          <span className="ff-qlive__pin ff-qlive__pin--o" aria-hidden="true" />
          <span className="ff-qlive__k">Pickup</span>
          <span className="ff-qlive__v">{origin || "Not set yet"}</span>
          {pickup ? <span className="ff-qlive__s"><time dateTime={pickup.iso}>{pickup.text}</time></span> : null}
        </li>
        <li className={destination ? "is-set" : undefined}>
          <span className="ff-qlive__pin ff-qlive__pin--d" aria-hidden="true" />
          <span className="ff-qlive__k">Delivery</span>
          <span className="ff-qlive__v">{destination || "Not set yet"}</span>
        </li>
      </ol>
      <dl className="ff-qlive__facts">
        {facts.map((f) => (
          <div key={f.label}><dt>{f.label}</dt><dd className="ff-num">{f.value}</dd></div>
        ))}
      </dl>
      {foot ? <p className="ff-qlive__foot">{foot}</p> : null}
      {children}
    </section>
  );
}

/* ---------------------------------------------------------------- rate results */
/** "$1,234.56" → ["$1,234", ".56"] so the cents can sit smaller beside the hero number. */
export function splitMoney(s: string): [string, string] {
  const i = s.lastIndexOf(".");
  return i < 0 ? [s, ""] : [s.slice(0, i), s.slice(i)];
}

export type PickTagTone = "best" | "fast" | "guar" | "margin";
const toneOf = (t: string): PickTagTone => (t === "Best price" ? "best" : t === "Fastest" ? "fast" : t === "Best margin" ? "margin" : "guar");

/** `logoUrl`: the official logo the API serves for this carrier (`/v1/carrier-logos/...`); shown before the bundled file. */
type CarrierRef = { code: string; name: string; operatedBy?: string | null; logoUrl?: string | null; fallback?: "badge" | "monogram" };

/** Top-pick card: tags, logo + carrier, the price as the hero, delivery and transit, a note and the app's buttons. */
export function RatePickCard({ id, lead, tags, carrier, service, price, when, transit, note, extra, children }: {
  id: string;
  lead: boolean;
  tags: readonly string[];
  carrier: CarrierRef;
  service: string;
  /** Formatted price ("$1,234.56"); shown large, read through the buttons' labels. */
  price: string;
  when: string;
  transit: string;
  note: ReactNode;
  /** Staff extras (cost and margin) under the delivery line. */
  extra?: ReactNode;
  children?: ReactNode;
}) {
  const [whole, cents] = splitMoney(price);
  return (
    <article className={lead ? "ff-rr__pick is-lead" : "ff-rr__pick"} data-option={id}>
      <p className="ff-rr__picktags">
        {tags.map((t) => (
          <span key={t} className={`ff-rr__tag ff-rr__tag--${toneOf(t)}`}>{t}</span>
        ))}
      </p>
      <div className="ff-rr__pickhead">
        <CarrierLogo code={carrier.code} name={carrier.name} operatedBy={carrier.operatedBy} logoUrl={carrier.logoUrl} fallback={carrier.fallback} showName={false} />
        <div className="ff-rr__pickwho">
          {chipShowsName(carrier.code, carrier.name, carrier.operatedBy, carrier.fallback, carrier.logoUrl) ? null : <span className="ff-rr__pickname">{carrier.name}</span>}
          <span className="ff-rr__picksvc">{service}</span>
        </div>
      </div>
      <p className="ff-rr__pickprice" aria-hidden="true">
        <span className="ff-rr__pickwhole">{whole}</span><span className="ff-rr__pickcents">{cents}</span>
      </p>
      <p className="ff-rr__pickwhen">
        <span>{when}</span>
        <span className="ff-rr__pickdot" aria-hidden="true" />
        <span>{transit}</span>
      </p>
      {extra}
      <p className="ff-rr__picknote">{note}</p>
      <div className="ff-rr__pickbtns">{children}</div>
    </article>
  );
}

/** One row of the every-rate list: logo, carrier + service, meta, badges, delivery date and the app's side column. */
export function RateRow({ best, carrier, service, meta, badges, badgesDescribedBy, eta, etaLabel = "Delivers", side, children, entering = false }: {
  best: boolean;
  /** Just landed from the quote stream: fades in (opacity only; reduced motion shows it at once). */
  entering?: boolean;
  carrier: CarrierRef;
  service: string;
  meta: ReactNode;
  badges: ReactNode;
  badgesDescribedBy?: string;
  eta: ReactNode;
  etaLabel?: string;
  /** Price, staff numbers and buttons (right column). */
  side: ReactNode;
  /** More lines under the badges (pick reasons, notes). */
  children?: ReactNode;
}) {
  const hideName = chipShowsName(carrier.code, carrier.name, carrier.operatedBy, carrier.fallback, carrier.logoUrl);
  return (
    <li className={`ff-rate ff-rrow${best ? " is-best" : ""}${entering ? " ff-rrow--in" : ""}`}>
      <CarrierLogo code={carrier.code} name={carrier.name} operatedBy={carrier.operatedBy} logoUrl={carrier.logoUrl} fallback={carrier.fallback} showName={false} className="ff-rrow__logo" />
      <div className="ff-rate__main ff-rrow__main">
        <p className="ff-rrow__carrier">
          <span className={hideName ? "ff-rate__carrier ff-visually-hidden" : "ff-rate__carrier ff-rrow__name"}>{carrier.name}</span>
          <span className="ff-rrow__svc">{service}</span>
        </p>
        <p className="ff-rrow__meta">{meta}</p>
        <p className="ff-rate__badges ff-rrow__badges" aria-describedby={badgesDescribedBy}>{badges}</p>
        {children}
      </div>
      <p className="ff-rrow__eta">
        <span className="ff-rrow__etalabel">{etaLabel}</span>
        <span className="ff-num">{eta}</span>
      </p>
      <div className="ff-rate__side ff-rrow__side">{side}</div>
    </li>
  );
}

/** Segmented sort tabs (roving focus: arrows, Home, End). Panel id is `${idBase}-panel`. */
export function SortTabs<K extends string>({ label, items, value, onChange, idBase }: {
  label: string;
  /** `count` is shown beside the label when given (the portal's per-view counts). */
  items: ReadonlyArray<{ key: K; label: string; count?: number }>;
  value: K;
  onChange: (k: K) => void;
  idBase: string;
}) {
  const refs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = items.findIndex((v) => v.key === value);
    const n = items.length;
    const next =
      e.key === "ArrowRight" ? (i + 1) % n
      : e.key === "ArrowLeft" ? (i - 1 + n) % n
      : e.key === "Home" ? 0
      : e.key === "End" ? n - 1
      : -1;
    if (next < 0) return;
    e.preventDefault();
    const key = items[next]!.key;
    onChange(key);
    refs.current[key]?.focus();
  };
  return (
    <div className="ff-tabs ff-rr__seg ff-rr__seg--sort" role="tablist" aria-label={label}>
      {items.map((v) => (
        <button
          key={v.key}
          ref={(el) => {
            refs.current[v.key] = el;
          }}
          type="button"
          role="tab"
          id={`${idBase}-tab-${v.key}`}
          aria-selected={value === v.key}
          aria-controls={`${idBase}-panel`}
          tabIndex={value === v.key ? 0 : -1}
          className="ff-tab"
          onClick={() => onChange(v.key)}
          onKeyDown={onKey}
        >
          {v.count === undefined ? v.label : <>{v.label} <span className="ff-tab__count">{v.count}</span></>}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- rate filters */
export type RateFilters = {
  guaranteedOnly: boolean;
  /** Carrier keys the user hid. Keys not in the current quote are simply ignored. */
  hidden: string[];
};
export const NO_RATE_FILTERS: RateFilters = { guaranteedOnly: false, hidden: [] };
export type CarrierChoice = { code: string; name: string; count: number };
const MAX_HIDDEN = 50;

export function toggleHidden(f: RateFilters, code: string): RateFilters {
  return f.hidden.includes(code)
    ? { ...f, hidden: f.hidden.filter((c) => c !== code) }
    : { ...f, hidden: [...f.hidden, code].slice(-MAX_HIDDEN) };
}

/**
 * Rate filters on the shared one-row FilterToolbar: "Guaranteed only" first, the per-carrier show/hide chips
 * next; whatever doesn't fit moves into "More filters" (always on phones). "Clear filters" trails the row;
 * the rate count sits in the toolbar's `meta` slot (visible on phones too).
 */
export function RateFilterBar({ filters, carriers, shownCount, totalCount, onChange }: {
  filters: RateFilters;
  carriers: CarrierChoice[];
  shownCount: number;
  totalCount: number;
  onChange: (f: RateFilters) => void;
}) {
  const hiddenCarriers = carriers.filter((c) => filters.hidden.includes(c.code)).length;
  const active = filters.guaranteedOnly || hiddenCarriers > 0;
  const chips = (
    <ul className="ff-chips ff-rate-filters__carriers ff-qfilters__carriers" aria-label="Show or hide carriers">
      {carriers.map((c) => {
        const shown = !filters.hidden.includes(c.code);
        return (
          <li key={c.code}>
            <button
              type="button"
              className="ff-qfilters__chip"
              aria-pressed={shown}
              aria-label={`Show ${c.name} (${c.count} rate${c.count === 1 ? "" : "s"})`}
              title={c.name}
              onClick={() => onChange(toggleHidden(filters, c.code))}
            >
              <span className="ff-qfilters__name">{c.name}</span>
              <span className="ff-qfilters__n ff-num">{c.count}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
  return (
    <FilterToolbar
      label="Filter rates"
      className="ff-qfilters"
      anyActive={active}
      onReset={() => onChange(NO_RATE_FILTERS)}
      items={[
        {
          key: "guaranteed",
          priority: 1,
          active: filters.guaranteedOnly,
          inline: (
            <Toggle className="ff-qfilters__guar" label="Guaranteed only" checked={filters.guaranteedOnly} onChange={(guaranteedOnly) => onChange({ ...filters, guaranteedOnly })} />
          ),
        },
        ...(carriers.length > 1
          ? [{ key: "carriers", priority: 2, group: "Carriers", active: hiddenCarriers > 0, inline: chips }]
          : []),
      ]}
      trailing={
        active ? (
          <button type="button" className="ff-btn ff-btn--quiet ff-btn--sm ff-qfilters__clear" onClick={() => onChange(NO_RATE_FILTERS)}>
            Clear filters
          </button>
        ) : null
      }
      meta={<span>{active ? `Showing ${shownCount} of ${totalCount} rates` : `${totalCount} rates`}</span>}
    />
  );
}

/* ---------------------------------------------------------------- progressive rates */

/**
 * The slim line shown while a quote streams: a spinner, "Checking carriers…" and each carrier's running state
 * ("EagleLoad 12 rates · Old Dominion waiting"). The visible line changes with every rate, so it is not a live
 * region; a visually hidden status announces only carriers settling (`carrierDoneText`) and the end.
 */
export function RateStreamProgress({ carriers, done = false, rateCount }: {
  carriers: readonly StreamCarrier[];
  /** Every carrier has settled (the stream's `done` is in or about to be). */
  done?: boolean;
  /** Rates on screen so far, for the lead text. */
  rateCount: number;
}) {
  const announced = useRef(new Set<string>());
  const [announce, setAnnounce] = useState("");
  useEffect(() => {
    const lines: string[] = [];
    for (const c of carriers) {
      if (c.status === "waiting" || announced.current.has(c.carrier)) continue;
      announced.current.add(c.carrier);
      const t = carrierDoneText(c);
      if (t) lines.push(t);
    }
    if (done && !announced.current.has("\u0000done")) {
      announced.current.add("\u0000done");
      lines.push("Every carrier has answered.");
    }
    if (lines.length) setAnnounce(lines.join(" "));
  }, [carriers, done]);
  const text = carrierProgressText(carriers);
  return (
    <div className={done ? "ff-rsp ff-rsp--done" : "ff-rsp"} data-testid="rate-stream-progress">
      {done ? (
        <svg className="ff-rsp__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 8.5l3 3 6-6.5" /></svg>
      ) : (
        <span className="ff-rsp__spin" aria-hidden="true" />
      )}
      <span className="ff-rsp__lead">{done ? `${fmtCount(rateCount)} rate${rateCount === 1 ? "" : "s"} in` : rateCount > 0 ? `Checking carriers… ${fmtCount(rateCount)} so far` : "Checking carriers…"}</span>
      {text ? <p className="ff-rsp__carriers" aria-hidden="true">{text}</p> : null}
      <p className="ff-visually-hidden" role="status" aria-live="polite">{announce}</p>
    </div>
  );
}
